/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { all } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import ApiService from 'cio/services/api';
import { LinkInfo } from 'cio/utils/previews';
import { tracked } from '@glimmer/tracking';
import AnalyticsService from 'cio/services/analytics';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';

export const LINKS_BATCH_SIZE = 10;

const isSuccessCode = (code: number) => {
  return /^2[0-9][0-9]$/.test(String(code));
};

const isWarningCode = (code: number) => {
  return /^3[0-9][0-9]$/.test(String(code));
};

const isErrorCode = (code: number) => {
  return !isSuccessCode(code) && !isWarningCode(code);
};

const codeStatus = (code: number) => {
  if (isSuccessCode(code)) {
    return 'success';
  } else if (isWarningCode(code)) {
    return 'warning';
  }

  return 'error';
};

type ApiLinkStatus = {
  url: string;
  status_code: number;
  error: string;
  timeout: boolean;
};

type CheckedLink = {
  linkInfo: LinkInfo;
  linkStatus?: ApiLinkStatus | null;
  status?: string | null;
  errors?: string[] | null;
};

interface ReviewLinksArgs {
  editableEntity?: ComposerEditableEntity;
  links: LinkInfo[] | null;
  isLoading: boolean;
}

export default class ReviewLinks extends Component<ReviewLinksArgs> {
  @service analytics!: AnalyticsService;
  @service api!: ApiService;

  @tracked isReviewingLinks = false;
  @tracked hasCheckedLinkStatuses = false;

  @tracked links: CheckedLink[] = [];

  get hasBadLinks() {
    if (this.links.length === 0) {
      return false;
    }

    return this.links.some((link) => {
      return (
        link.linkInfo.errors.length > 0 ||
        (link.linkStatus?.status_code != null && isErrorCode(link.linkStatus?.status_code))
      );
    });
  }

  checkLinkStatuses = task({ drop: true }, async () => {
    this.hasCheckedLinkStatuses = false;

    let { links } = this.args;

    if (links == null || links.length === 0) {
      return;
    }

    let batches: LinkInfo[][] = [];

    let i = 0;

    while (i < links.length) {
      batches.push(links.slice(i, i + LINKS_BATCH_SIZE));
      i += LINKS_BATCH_SIZE;
    }

    let tasks = batches.map((batch) => this.checkLinksBatch.perform(batch));

    let linksWithStatuses = await all(tasks);

    this.hasCheckedLinkStatuses = true;
    this.links = linksWithStatuses.reduce((arr, item) => arr.concat(item), []);
  });

  checkLinksBatch = task({ enqueue: true, maxConcurrency: 1 }, async (links: LinkInfo[]) => {
    let { urls }: { urls: ApiLinkStatus[] } = await this.api.post('previews/link_statuses', {
      data: {
        urls: links.mapBy('url'),
      },
    });

    return links.map((link) => {
      let url = urls.findBy('url', link.url);

      if (url == null) {
        return {
          linkInfo: link,
        };
      }

      let errors = [...link.errors];

      if (isPresent(url.error)) {
        errors.push(url.error);
      }

      return {
        linkInfo: link,
        linkStatus: url,
        errors,
        status: codeStatus(url.status_code),
      };
    });
  });

  private resetLinkStatuses() {
    this.hasCheckedLinkStatuses = false;
    this.links = (this.args.links ?? []).map((linkInfo) => ({ linkInfo, errors: [...(linkInfo.errors ?? [])] }));
  }

  @action
  reviewLinks() {
    this.isReviewingLinks = true;
    this.resetLinkStatuses();
    this.analytics.track('message', 'review_links_viewed', this.args.editableEntity?.analyticsData);
  }
}
